import { AdminSidebar, SIDEBAR_WIDTH_KEY, DEFAULT_WIDTH } from "@/components/AdminSidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import { hasAnyRole } from "@/lib/roles";
import { useIsMobile } from "@/hooks/useMobile";
import { Plus, Pencil, Trash2, Clock, Calendar } from "lucide-react";
import { useState, CSSProperties } from "react";
import { toast } from "sonner";

const DAYS_OF_WEEK = [
  { value: "1", label: "Montag" },
  { value: "2", label: "Dienstag" },
  { value: "3", label: "Mittwoch" },
  { value: "4", label: "Donnerstag" },
  { value: "5", label: "Freitag" },
  { value: "6", label: "Samstag" },
  { value: "0", label: "Sonntag" },
];

interface TemplateFormData {
  serviceId: number | null;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  instructorId: number | null;
  isActive: boolean;
}

export default function GymTemplates() {
  const { loading, user } = useAuth();
  const { data: config } = trpc.config.getTenantConfig.useQuery();
  const isMobile = useIsMobile();
  
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    return saved ? parseInt(saved, 10) : DEFAULT_WIDTH;
  });

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<number | null>(null);
  const [formData, setFormData] = useState<TemplateFormData>({
    serviceId: null,
    dayOfWeek: 1,
    startTime: "08:00",
    endTime: "18:00",
    instructorId: null,
    isActive: true
  });

  const utils = trpc.useUtils();
  const { data: templates, isLoading } = trpc.gym.templates.list.useQuery({});
  const { data: services } = trpc.gym.services.list.useQuery({});
  const { data: instructorsList } = trpc.instructor.list.useQuery();
  
  const createMutation = trpc.gym.templates.create.useMutation({
    onSuccess: () => {
      toast.success("Vorlage erstellt");
      utils.gym.templates.list.invalidate();
      setIsCreateOpen(false);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const updateMutation = trpc.gym.templates.update.useMutation({
    onSuccess: () => {
      toast.success("Vorlage aktualisiert");
      utils.gym.templates.list.invalidate();
      setEditingTemplate(null);
      resetForm();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const deleteMutation = trpc.gym.templates.delete.useMutation({
    onSuccess: () => {
      toast.success("Vorlage gelöscht");
      utils.gym.templates.list.invalidate();
    },
    onError: (error) => {
      toast.error(error.message);
    }
  });

  const resetForm = () => {
    setFormData({
      serviceId: null,
      dayOfWeek: 1,
      startTime: "08:00",
      endTime: "18:00",
      instructorId: null,
      isActive: true
    });
  };

  const handleCreate = () => {
    if (!formData.serviceId) {
      toast.error("Bitte wählen Sie einen Service");
      return;
    }
    createMutation.mutate({
      serviceId: formData.serviceId,
      dayOfWeek: formData.dayOfWeek,
      startTime: formData.startTime,
      endTime: formData.endTime,
      instructorId: formData.instructorId || undefined
    });
  };

  const handleUpdate = () => {
    if (!editingTemplate) return;
    updateMutation.mutate({
      id: editingTemplate,
      serviceId: formData.serviceId || undefined,
      dayOfWeek: formData.dayOfWeek,
      startTime: formData.startTime,
      endTime: formData.endTime,
      instructorId: formData.instructorId,
      isActive: formData.isActive
    });
  };

  const handleEdit = (template: NonNullable<typeof templates>[0]) => {
    setFormData({
      serviceId: template.serviceId,
      dayOfWeek: template.dayOfWeek,
      startTime: template.startTime,
      endTime: template.endTime,
      instructorId: template.instructorId || null,
      isActive: template.isActive
    });
    setEditingTemplate(template.id);
  };

  const handleDelete = (id: number) => {
    if (confirm("Möchten Sie diese Vorlage wirklich löschen?")) {
      deleteMutation.mutate({ id });
    }
  };

  const getDayLabel = (dayOfWeek: number) => {
    return DAYS_OF_WEEK.find(d => d.value === String(dayOfWeek))?.label || "Unbekannt";
  };

  // Auth check
  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Laden...</div>;
  }

  if (!user || !hasAnyRole(user.roles, ["admin"])) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-8 p-8 max-w-md w-full">
          <h1 className="text-2xl font-semibold tracking-tight text-center">
            Anmeldung erforderlich
          </h1>
          <p className="text-sm text-muted-foreground text-center max-w-sm">
            Der Zugriff auf diesen Bereich erfordert Administratorrechte.
          </p>
          <Button
            onClick={() => {
              window.location.href = getLoginUrl(config?.authProvider);
            }}
            size="lg"
            className="w-full"
          >
            Anmelden
          </Button>
        </div>
      </div>
    );
  }

  const TemplateForm = ({ isEdit = false }: { isEdit?: boolean }) => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="service">Service *</Label>
        <Select
          value={formData.serviceId?.toString() || ""}
          onValueChange={(value) => setFormData({ ...formData, serviceId: parseInt(value) })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Service auswählen" />
          </SelectTrigger>
          <SelectContent>
            {services?.filter(s => s.isActive).map(service => (
              <SelectItem key={service.id} value={service.id.toString()}>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full" 
                    style={{ backgroundColor: service.color }}
                  />
                  {service.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div className="space-y-2">
        <Label htmlFor="dayOfWeek">Wochentag *</Label>
        <Select
          value={formData.dayOfWeek.toString()}
          onValueChange={(value) => setFormData({ ...formData, dayOfWeek: parseInt(value) })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Wochentag auswählen" />
          </SelectTrigger>
          <SelectContent>
            {DAYS_OF_WEEK.map(day => (
              <SelectItem key={day.value} value={day.value}>
                {day.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="startTime">Startzeit *</Label>
          <Input
            id="startTime"
            type="time"
            value={formData.startTime}
            onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="endTime">Endzeit *</Label>
          <Input
            id="endTime"
            type="time"
            value={formData.endTime}
            onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="instructor">Kursleiter (optional)</Label>
        <Select
          value={formData.instructorId?.toString() || "none"}
          onValueChange={(value) => setFormData({ ...formData, instructorId: value === "none" ? null : parseInt(value) })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Kein Kursleiter" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Kein Kursleiter</SelectItem>
            {instructorsList?.filter((i: { isActive: boolean }) => i.isActive).map((instructor: { id: number; name: string }) => (
              <SelectItem key={instructor.id} value={instructor.id.toString()}>
                {instructor.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isEdit && (
        <div className="flex items-center justify-between">
          <Label htmlFor="isActive">Aktiv</Label>
          <Switch
            id="isActive"
            checked={formData.isActive}
            onCheckedChange={(checked) => setFormData({ ...formData, isActive: checked })}
          />
        </div>
      )}
    </div>
  );

  return (
    <SidebarProvider
      style={{ "--sidebar-width": `${sidebarWidth}px` } as CSSProperties}
    >
      <AdminSidebar setSidebarWidth={setSidebarWidth} />
      <SidebarInset>
        {isMobile && (
          <div className="flex border-b h-14 items-center justify-between bg-background/95 px-2 backdrop-blur supports-[backdrop-filter]:backdrop-blur sticky top-0 z-40">
            <div className="flex items-center gap-2">
              <SidebarTrigger className="h-9 w-9 rounded-lg bg-background" />
              <span className="tracking-tight text-foreground font-medium">Slot-Vorlagen</span>
            </div>
          </div>
        )}
        <main className="flex-1 p-4">
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Slot-Vorlagen</h1>
                <p className="text-muted-foreground">
                  Definieren Sie wiederkehrende Zeitfenster für die automatische Slot-Generierung
                </p>
              </div>
              <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogTrigger asChild>
                  <Button onClick={resetForm}>
                    <Plus className="h-4 w-4 mr-2" />
                    Neue Vorlage
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Neue Vorlage erstellen</DialogTitle>
                    <DialogDescription>
                      Erstellen Sie eine Vorlage für wiederkehrende Zeitslots
                    </DialogDescription>
                  </DialogHeader>
                  <TemplateForm />
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                      Abbrechen
                    </Button>
                    <Button 
                      onClick={handleCreate}
                      disabled={!formData.serviceId || createMutation.isPending}
                    >
                      {createMutation.isPending ? "Erstelle..." : "Erstellen"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {/* Info Card */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="py-4">
                <p className="text-sm text-blue-800">
                  <strong>Hinweis:</strong> Vorlagen definieren, wann Slots verfügbar sein sollen. 
                  Gehen Sie danach zum <strong>Slot-Kalender</strong> und klicken Sie auf "Slots generieren", 
                  um basierend auf diesen Vorlagen tatsächliche buchbare Slots zu erstellen.
                </p>
              </CardContent>
            </Card>

            {/* Templates List */}
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">Laden...</div>
            ) : templates?.length === 0 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">Noch keine Vorlagen vorhanden</p>
                  <Button onClick={() => setIsCreateOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Erste Vorlage erstellen
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {templates?.map(template => (
                  <Card key={template.id} className={!template.isActive ? "opacity-60" : ""}>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-4 h-4 rounded-full" 
                            style={{ backgroundColor: template.service?.color || "#888" }}
                          />
                          <div>
                            <CardTitle className="text-lg">{template.service?.name || "Unbekannt"}</CardTitle>
                            <CardDescription>{getDayLabel(template.dayOfWeek)}</CardDescription>
                          </div>
                        </div>
                        {!template.isActive && (
                          <span className="text-xs bg-muted px-2 py-1 rounded">Inaktiv</span>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <Clock className="h-4 w-4" />
                        {template.startTime} - {template.endTime}
                      </div>
                      {template.instructor && (
                        <p className="text-sm text-muted-foreground mb-3">
                          Kursleiter: {template.instructor.name}
                        </p>
                      )}
                      <div className="flex gap-2">
                        <Dialog open={editingTemplate === template.id} onOpenChange={(open) => {
                          if (!open) {
                            setEditingTemplate(null);
                            resetForm();
                          }
                        }}>
                          <DialogTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleEdit(template)}
                            >
                              <Pencil className="h-4 w-4 mr-1" />
                              Bearbeiten
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle>Vorlage bearbeiten</DialogTitle>
                              <DialogDescription>
                                Bearbeiten Sie die Details der Vorlage
                              </DialogDescription>
                            </DialogHeader>
                            <TemplateForm isEdit />
                            <DialogFooter>
                              <Button variant="outline" onClick={() => {
                                setEditingTemplate(null);
                                resetForm();
                              }}>
                                Abbrechen
                              </Button>
                              <Button 
                                onClick={handleUpdate}
                                disabled={!formData.serviceId || updateMutation.isPending}
                              >
                                {updateMutation.isPending ? "Speichere..." : "Speichern"}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleDelete(template.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}
